import { FormQuestionOption } from './form-question-option';
import { FormQuestionType, FormQuestionTypeEnum } from './form-question-type';

const formTypes = FormQuestionTypeEnum;

/**
 * Class to map form builder form elements (Form Array > Form Group >  input values],
 * to the matching api FormQuestion class. Comes back from db, maps to FormlyQuestion,
 * and from there, Formly Question renders the form to the user.
 */
export class FormQuestion
{
    id: number;
    formId: number;
    sortOrder: number;
    key: string;
    label: string;
    description: string;
    isRequired?: boolean;
    isEnabled?: boolean;
    isMultiselect?: boolean;
    type: string;
    options: FormQuestionOption[] = [];
    icon?: string;

    /**
     * Constructor, comes from a form group set of values, or from the database.
     *
     * @param request
     */
    constructor(request: any) {
        this.id = request.id || 0;
        this.formId = request.formId || 0;
        this.sortOrder = request.sortOrder ?? 1;
        this.key = request.key || '';
        this.label = request.label || '';
        this.description = request.description || '';
        this.isRequired = request.isRequired ?? false;
        this.isEnabled = request.isEnabled ?? false;
        this.isMultiselect = request.isMultiselect ?? false;

        // it will take 'typeName' if coming from the create form; or 'type' as string coming from database
        this.type = request.typeName ?? request.type;
        if (this.type === 'multiselect') {
            // multiselect is not a real type
            this.type = 'select';
            this.isMultiselect = true;
        }

        if (request.options && request.options.length) {
            this.options = request.options.map(o => new FormQuestionOption(o));
        }

        // set the icon here for easy display when re-rendering Questions with Answers
        this.icon = formTypes.find((ft: FormQuestionType) => ft.type === this.type).icon;
    }
}

/**
 * Resolves a stored answer for display: a raw option VALUE resolves to its current label;
 * anything matching no option (free text, or a question with no options at all) is returned
 * exactly as stored — never blanked, never dropped (SD-6260).
 *
 * Matches by VALUE ONLY, never by label. Every answer is stored as the raw submitted value,
 * never a label — matching by label as a fallback would risk resolving to the WRONG option
 * whenever one option's label happens to equal a different option's value (verified against
 * production data during review: value=1/label="0" and value=2/label="1" on the same
 * question — a stored "1" must resolve to the second option's label "1", not collide with the
 * first option by treating "1" as a label). Mirrors FormsService::resolveAnswerForDisplay() on
 * the backend.
 *
 * Split on isMultiselect, not on whether the raw answer happens to contain a comma — a
 * single-select (or never-select) answer can legitimately contain commas and must never be
 * shredded or blanked. When isMultiselect doesn't say multiselect, a single-value match is
 * tried FIRST, falling back to a comma split only when EVERY resulting piece resolves to a
 * real option (a legacy multiselect question created before that flag existed can still hold
 * a genuinely comma-joined answer under a falsy flag).
 *
 * Deliberately NOT gated on type === 'select': a question's options survive a type change
 * (disabled, never deleted) specifically so a historical answer can still resolve to its
 * label after the question becomes a text field or anything else — gating on the CURRENT
 * type would return the answer's raw stored value instead, defeating that (SD-6260).
 */
export function resolveAnswerDisplay(question: FormQuestion, answer: string): string {
    if (!question.options || !question.options.length) {
        return answer;
    }

    // FormQuestionOption.value is typed number but is a stringly-stored DB value in practice
    // (e.g. "1", "blue") — compare as strings, matching how the value actually arrives from
    // the server and from a split() answer.
    const resolveToken = (value: string) => question.options.find(o => o.value?.toString() === value);

    if (question.isMultiselect) {
        const resolved = answer.split(',').map((value) => {
            const option = resolveToken(value);

            return option ? option.label : value;
        });

        return resolved.join(', ');
    }

    const option = resolveToken(answer);
    if (option) {
        return option.label;
    }

    if (answer.includes(',')) {
        const resolvedPieces = answer.split(',').map(resolveToken);
        if (resolvedPieces.every(o => !!o)) {
            return resolvedPieces.map(o => o.label).join(', ');
        }
    }

    return answer;
}
